import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, User, Clock, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SupportMessage {
  id: string;
  chat_id: string;
  sender_type: string;
  sender_name: string | null;
  sender_email: string | null;
  message: string;
  created_at: string;
  is_read: boolean;
}

interface SupportChat {
  id: string;
  customer_email: string;
  customer_name: string | null;
  status: string;
  priority: string;
  subject: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  assigned_admin_id: string | null;
  is_closed: boolean;
}

export const LiveSupportManagement = () => {
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<SupportChat | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
    }
  }, [selectedChat]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollAreaRef.current) {
        const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      }
    };
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  useEffect(() => {
    const chatsChannel = supabase
      .channel(`support_chats_admin-${crypto.randomUUID()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_chats' }, () => {
        loadChats();
      })
      .subscribe();

    const messagesChannel = supabase
      .channel(`support_messages_admin-${crypto.randomUUID()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages' }, (payload) => {
        const newMsg = payload.new as SupportMessage;
        if (selectedChat && newMsg.chat_id === selectedChat.id) {
          setMessages(prev => [...prev, newMsg]);
        }
        if (newMsg.sender_type === 'customer') {
          toast({
            title: "New customer message",
            description: `From: ${newMsg.sender_name || newMsg.sender_email}`,
          });
          reopenClosedChat(newMsg.chat_id);
        }
        loadChats();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(chatsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [selectedChat, toast]);

  const loadChats = async () => {
    try {
      const { data } = await supabase
        .from('support_chats')
        .select('*')
        .order('last_message_at', { ascending: false });
      if (data) setChats(data);
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
      if (data) {
        setMessages(data);
        await supabase
          .from('support_messages')
          .update({ is_read: true })
          .eq('chat_id', chatId)
          .eq('sender_type', 'customer')
          .eq('is_read', false);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    setIsLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Authentication failed');

      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          chat_id: selectedChat.id,
          sender_type: 'admin',
          sender_name: 'Support Team',
          message: newMessage.trim()
        })
        .select();

      if (error) throw new Error(`Database error: ${error.message}`);
      if (!data || data.length === 0) throw new Error('Message was not saved');

      setNewMessage("");
      toast({ title: "Message sent", description: "Your reply has been delivered." });
    } catch (error) {
      toast({
        title: "Failed to send message",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateChatStatus = async (chatId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('support_chats')
        .update({ status })
        .eq('id', chatId);
      if (error) throw error;

      setChats(prev => prev.map(chat =>
        chat.id === chatId ? { ...chat, status } : chat
      ));
      if (selectedChat && selectedChat.id === chatId) {
        setSelectedChat(prev => prev ? { ...prev, status } : null);
      }
      toast({ title: "Status updated", description: `Chat status changed to ${status}` });
    } catch (error) {
      toast({ title: "Error updating status", description: "Please try again.", variant: "destructive" });
    }
  };

  const closeChat = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from('support_chats')
        .update({ is_closed: true, status: 'closed' })
        .eq('id', chatId);
      if (error) throw error;

      loadChats();
      if (selectedChat && selectedChat.id === chatId) {
        setSelectedChat(null);
      }
      toast({ title: "Chat closed", description: "The chat has been closed." });
    } catch (error) {
      toast({ title: "Error closing chat", description: "Please try again.", variant: "destructive" });
    }
  };

  const reopenClosedChat = async (chatId: string) => {
    try {
      await supabase
        .from('support_chats')
        .update({ is_closed: false, status: 'in_progress' })
        .eq('id', chatId)
        .eq('is_closed', true);
      setActiveTab('in_progress');
    } catch (error) {
      console.error('Error reopening chat:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new': return <AlertCircle className="h-3 w-3 text-destructive" />;
      case 'in_progress': return <Clock className="h-3 w-3 text-yellow-500" />;
      case 'replied': return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      default: return <MessageCircle className="h-3 w-3" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'destructive';
      case 'in_progress': return 'secondary';
      case 'replied': return 'default';
      default: return 'outline';
    }
  };

  const filterChatsByStatus = (status: string) => {
    switch (status) {
      case 'new': return chats.filter(c => c.status === 'new' && !c.is_closed);
      case 'in_progress': return chats.filter(c => c.status === 'in_progress' && !c.is_closed);
      case 'replied': return chats.filter(c => c.status === 'replied' && !c.is_closed);
      case 'closed': return chats.filter(c => c.is_closed);
      case 'all': return chats.filter(c => !c.is_closed);
      default: return chats;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Mobile: show chat list OR messages, not both
  const showChatView = !!selectedChat;

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Chat List - hidden on mobile when a chat is selected */}
      <div className={`${showChatView ? 'hidden md:block' : 'block'} w-full md:w-1/3 border-r border-border flex flex-col`}>
        <div className="p-3 border-b border-border">
          <h3 className="font-semibold mb-2 text-sm">Support Chats</h3>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full h-auto grid grid-cols-5 gap-0.5 bg-muted p-0.5">
              <TabsTrigger value="new" className="text-xs px-1 py-1.5">New</TabsTrigger>
              <TabsTrigger value="in_progress" className="text-xs px-1 py-1.5">Active</TabsTrigger>
              <TabsTrigger value="replied" className="text-xs px-1 py-1.5">Replied</TabsTrigger>
              <TabsTrigger value="closed" className="text-xs px-1 py-1.5">Closed</TabsTrigger>
              <TabsTrigger value="all" className="text-xs px-1 py-1.5">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1.5">
            {filterChatsByStatus(activeTab).map((chat) => (
              <Card
                key={chat.id}
                className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                  selectedChat?.id === chat.id ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => setSelectedChat(chat)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium text-xs truncate">
                        {chat.customer_name || chat.customer_email}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {getStatusIcon(chat.status)}
                      <Badge variant={getStatusColor(chat.status) as any} className="text-[10px] px-1.5 py-0">
                        {chat.status}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {chat.subject || 'No subject'}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(chat.last_message_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
            {filterChatsByStatus(activeTab).length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-8">No chats in this category</p>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Messages */}
      <div className={`${showChatView ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
        {selectedChat ? (
          <>
            {/* Header */}
            <div className="p-2.5 md:p-4 border-b border-border">
              <div className="flex items-center gap-2">
                {/* Back button on mobile */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden shrink-0 px-2"
                  onClick={() => setSelectedChat(null)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">
                    {selectedChat.customer_name || selectedChat.customer_email}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    {selectedChat.subject || 'No subject'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Select
                    value={selectedChat.status}
                    onValueChange={(value) => updateChatStatus(selectedChat.id, value)}
                  >
                    <SelectTrigger className="w-24 md:w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-8 hidden sm:inline-flex"
                    onClick={() => closeChat(selectedChat.id)}
                  >
                    Close
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-8 sm:hidden px-2"
                    onClick={() => closeChat(selectedChat.id)}
                  >
                    ✕
                  </Button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-3 md:p-4" ref={scrollAreaRef}>
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] md:max-w-[70%] p-2.5 rounded-lg ${
                        message.sender_type === 'admin'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="font-medium text-xs mb-0.5">
                        {message.sender_type === 'admin' ? 'Support Team' : message.sender_name || 'Customer'}
                      </div>
                      <div className="text-sm whitespace-pre-wrap break-words">{message.message}</div>
                      <div className="text-[10px] opacity-70 mt-1">
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-2.5 md:p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  className="text-sm h-9"
                />
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || !newMessage.trim()}
                  size="sm"
                  className="shrink-0 h-9 w-9 p-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Select a chat to start</h3>
              <p className="text-muted-foreground">
                Choose a chat from the list to view and respond to messages
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
